<template>
  <div
    :class="{'paginator--blur': scrollTop >= 5}"
    class="paginator"
    :style="{'width': paginatorWidth + 'px'}"
  >
    <div class="paginator__control">
      <button
        :disabled="!newsFeedStore.currentPage"
        class="paginator-previous__icon paginator__icon"
        :class="{'paginator__icon--disabled': currentPage === 1}"
        @click="prevPage"
      >
        <ArrowLeftIcon />
      </button>
      <div class="paginator-current">
        <div class="paginator-current__page">
          <input
            v-model="currentPage"
            type="number"
          >
        </div>
        /
        <div>
          {{ totalPages }}
        </div>
      </div>
      <button
        :disabled="newsFeedStore.totalCount - newsFeedStore.offset < 20"
        class="paginator-next__icon paginator__icon"
        :class="{'paginator__icon--disabled': currentPage === totalPages}"
        @click="nextPage"
      >
        <ArrowRightIcon />
      </button>
    </div>

    <div class="paginator__info">
      <div class="paginator-total-page">
        <div class="paginator-total-page__number">
          <input
            v-model="newsFeedStore.take"
            type="number"
            @change="updateItemsPerPage"
          >
        </div>
      </div>
      <div class="paginator__total-message">
        {{ t('news_page_pagination.on_page') }} {{ newsFeedStore.totalCount }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { t } from "@/controllerLK/AvalLocalization";
import { computed, defineProps, onBeforeUnmount, ref, toRef, watch } from "vue";
import { debounce } from "@/functions/debouncer";
import ArrowLeftIcon from "@/components/Icons/ArrowLeftIcon.vue";
import ArrowRightIcon from "@/components/Icons/ArrowRightIcon.vue";

const props = defineProps({
  container: {
    type: [Object, null],
    required: true
  }
});

const newsFeedStore = useNewsFeedStore();
const container = toRef(props, "container");

// Расчет ширины пагинатора под разный масштаб
const paginatorWidth = ref(0);
let ro;

// Расчет скролла для наложения блюра на пагинатор
const scrollTop = ref(0);

const onScroll = (e) => {
  const el = e.target || e;
  scrollTop.value = el.scrollHeight - el.scrollTop - el.clientHeight;
};

watch(container, (el, prev) => {
  // убираем старый наблюдатель
  if (prev && ro) {
    ro.disconnect();
  }
  // вешаем новый
  if (el) {
    ro = new ResizeObserver(([entry]) => {
      // Округление вверх 2-ух знаков после запятой
      const raw = entry.contentRect.width;
      const roundedUp = Math.ceil(raw * 100) / 100;
      paginatorWidth.value = parseFloat(roundedUp.toFixed(2));
    });
    ro.observe(el);
  }
}, { immediate: true });

onBeforeUnmount(() => {
  ro?.disconnect();
});

watch(container, (el, prev) => {
  if (prev) prev.removeEventListener("scroll", onScroll);
  if (el) {
    onScroll(el);
    el.addEventListener("scroll", onScroll, { passive: true });
  }
}, { immediate: true });

// Пагинатор
const totalPages = computed(
    () => newsFeedStore.take > 0 ? Math.ceil(newsFeedStore.totalCount / newsFeedStore.take) : 1);

const updateItemsPerPage = () => {
  if (newsFeedStore.take < 1) {
    newsFeedStore.take = 1;
  } else if (newsFeedStore.take > 100) {
    newsFeedStore.take = 100;
  }
  newsFeedStore.currentPage = 0;
  updateNews(newsFeedStore.searchValue);
};

const currentPage = computed({
  get: () => newsFeedStore.currentPage + 1,
  set: val => {
    const clamped = Math.max(1, Math.min(val, totalPages.value));
    newsFeedStore.currentPage = clamped - 1;
    updateNews(newsFeedStore.searchValue);
  }
});

const nextPage = () => {
  if (newsFeedStore.currentPage < totalPages.value - 1) {
    newsFeedStore.currentPage++;
    updateNews(newsFeedStore.searchValue);
  }
};

const prevPage = () => {
  if (newsFeedStore.currentPage > 0) {
    newsFeedStore.currentPage--;
    updateNews(newsFeedStore.searchValue);
  }
};

const updateNews = debounce(async (searchValue) => {
  if (container.value) container.value.scrollTop = 0; // Прокручиваем контейнер в начало, чтобы показать первую новость
  await newsFeedStore.getNews(searchValue);
}, 500);
</script>
<style lang="scss" scoped>
.paginator {
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 10px 2px 2px;
  color: var(--text-text-pur);
  gap: 10px;
  bottom: 0;

  &--blur {
    background: linear-gradient(180deg, rgba(238, 241, 244, 0.69) 0%, #EEF1F4 100%);
    box-shadow: 0 -5px 7px 0 #7090B014;
    backdrop-filter: blur(6px);
  }

  &__control {
    display: flex;
    align-items: center;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: default;
  }

  &-total-page {
    display: flex;
    align-items: center;
    gap: 5px;

    &__number {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      color: var(--text-text-pur);
      border-radius: 100%;
      background: #FFFFFF;

      input {
        width: 100%;
        text-align: center;
        background: transparent;

        &:focus {
          outline: none;
        }
      }

      &:focus-within {
        border: 1px solid #CACACA;
      }

      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        margin: 0;
        -webkit-appearance: none;
      }

      input[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
      }
    }
  }

  &-current {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: default;

    &__page {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 100%;
      background: #FFFFFF;
      color: #7B87A5;
      flex-wrap: wrap;

      input {
        width: 100%;
        text-align: center;
        background: transparent;

        &:focus {
          outline: none;
        }
      }

      &:focus-within {
        border: 1px solid #CACACA;
      }

      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        margin: 0;
        -webkit-appearance: none;
      }

      input[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
      }
    }
  }

  &__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 30px;
    cursor: pointer;
    user-select: none;

    &--disabled {
      color: #CACACA;
      cursor: default;
    }
  }
}
</style>